import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SearchIndexEntry } from '@claude-code-insights/shared';
import { fetchSearchIndex } from '../lib/api.js';

interface GraphNode {
  id: string;
  title: string;
  category: string;
  relevanceScore: number;
  tags: string[];
  createdAt: string;
  x: number;
  y: number;
  el?: SVGCircleElement;
}

interface GraphLink {
  source: GraphNode;
  target: GraphNode;
  weight: number;
  el?: SVGLineElement;
}

interface ClusterInfo {
  category: string;
  cx: number;
  cy: number;
  haloR: number;
  nodes: GraphNode[];
}

const CATEGORY_COLORS: Record<string, string> = {
  'mcp-server': '#4caf50',
  'hook': '#9d8fff',
  'workflow': '#ff9800',
  'claude-md-pattern': '#c084cc',
  'prompting-technique': '#e6c229',
  'skill': '#7f6df2',
  'tool-usage': '#38bdf8',
  'configuration': '#fb7c52',
  'general-tip': '#94a3b8',
  'model-comparison': '#f472b6',
  'api-integration': '#34d399',
  'agent-framework': '#e879f9',
  'rag-pattern': '#86efac',
};

function getCategoryColor(cat: string): string {
  return CATEGORY_COLORS[cat] ?? '#888';
}

// Power curve: score 3 → ~4px, score 5 → ~7px, score 7 → ~11px, score 10 → ~19px
// Gives clearly visible size differences across the 0-10 range
function nodeRadius(score: number): number {
  return Math.pow(score, 1.5) * 0.6;
}

// Interpolate a hex category colour toward a dark muted neutral as the node ages.
// fadeFactor 0 = full vibrant colour, 1 = fully faded (#2a2d35)
function fadeColor(hex: string, fadeFactor: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const tr = 42, tg = 45, tb = 53; // fade target — dark blue-grey
  return `rgb(${Math.round(r + (tr - r) * fadeFactor)},${Math.round(g + (tg - g) * fadeFactor)},${Math.round(b + (tb - b) * fadeFactor)})`;
}

// Returns 0 for the newest node, up to 0.78 for the oldest.
// Power curve keeps recent nodes bright longer before fading.
function ageFade(createdAt: string, newestMs: number, rangeMs: number): number {
  if (rangeMs === 0) return 0;
  const ageMs = newestMs - new Date(createdAt).getTime();
  return Math.pow(Math.min(ageMs / rangeMs, 1), 0.7) * 0.78;
}

// Sunflower spiral — evenly distributes N points inside a circle of radius maxR
function spiralPos(index: number, total: number, maxR: number): [number, number] {
  if (total === 1) return [0, 0];
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const r = Math.sqrt((index + 0.5) / total) * maxR;
  const theta = index * goldenAngle;
  return [r * Math.cos(theta), r * Math.sin(theta)];
}

function computeLayout(nodes: GraphNode[], width: number, height: number): ClusterInfo[] {
  // Group by category
  const groups = new Map<string, GraphNode[]>();
  for (const node of nodes) {
    if (!groups.has(node.category)) groups.set(node.category, []);
    groups.get(node.category)!.push(node);
  }

  const categories = [...groups.keys()];
  const N = categories.length;
  const cols = Math.ceil(Math.sqrt(N));
  const rows = Math.ceil(N / cols);

  const padX = width * 0.08;
  const padY = height * 0.1;
  const cellW = (width - padX * 2) / cols;
  const cellH = (height - padY * 2) / rows;
  const haloR = Math.min(cellW, cellH) * 0.42;

  const clusters: ClusterInfo[] = [];

  categories.forEach((cat, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const cx = padX + cellW * col + cellW / 2;
    const cy = padY + cellH * row + cellH / 2;
    const catNodes = groups.get(cat)!;

    catNodes.forEach((node, j) => {
      const [dx, dy] = spiralPos(j, catNodes.length, haloR * 0.88);
      node.x = cx + dx;
      node.y = cy + dy;
    });

    clusters.push({ category: cat, cx, cy, haloR, nodes: catNodes });
  });

  return clusters;
}

function buildGraph(entries: SearchIndexEntry[]): { nodes: GraphNode[]; links: GraphLink[] } {
  const nodes: GraphNode[] = entries.map((e) => ({
    id: e.id,
    title: e.title,
    category: e.category,
    relevanceScore: e.relevanceScore,
    tags: e.tags ?? [],
    createdAt: e.createdAt,
    x: 0,
    y: 0,
  }));

  const links: GraphLink[] = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const shared = nodes[i].tags.filter((t) => nodes[j].tags.includes(t)).length;
      // Only intra-cluster edges (same category) to avoid cross-cluster noise
      if (shared >= 3 && nodes[i].category === nodes[j].category) {
        links.push({ source: nodes[i], target: nodes[j], weight: shared });
      }
    }
  }

  return { nodes, links };
}

const MAX_GRAPH_NODES = 200;

export default function GraphView() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [allEntries, setAllEntries] = useState<SearchIndexEntry[]>([]);
  const [filteredCategory, setFilteredCategory] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{ node: GraphNode; x: number; y: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchIndex()
      .then((entries) => {
        setAllEntries(entries);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!svgRef.current || allEntries.length === 0) return;

    const filtered = filteredCategory
      ? allEntries.filter((e) => e.category === filteredCategory)
      : allEntries;
    const entries = filtered
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, MAX_GRAPH_NODES);

    const svgEl = svgRef.current;
    const rect = svgEl.getBoundingClientRect();
    const width = rect.width || svgEl.clientWidth || 900;
    const height = rect.height || svgEl.clientHeight || 600;
    while (svgEl.firstChild) svgEl.removeChild(svgEl.firstChild);

    const { nodes, links } = buildGraph(entries);
    const clusters = computeLayout(nodes, width, height);

    const svgNS = 'http://www.w3.org/2000/svg';

    // ── Defs: glow filter ───────────────────────────────────────────────────
    const defs = document.createElementNS(svgNS, 'defs');
    const filter = document.createElementNS(svgNS, 'filter');
    filter.setAttribute('id', 'node-glow');
    filter.setAttribute('x', '-60%');
    filter.setAttribute('y', '-60%');
    filter.setAttribute('width', '220%');
    filter.setAttribute('height', '220%');
    const blur = document.createElementNS(svgNS, 'feGaussianBlur');
    blur.setAttribute('in', 'SourceGraphic');
    blur.setAttribute('stdDeviation', '5');
    blur.setAttribute('result', 'coloredBlur');
    const merge = document.createElementNS(svgNS, 'feMerge');
    const m1 = document.createElementNS(svgNS, 'feMergeNode');
    m1.setAttribute('in', 'coloredBlur');
    const m2 = document.createElementNS(svgNS, 'feMergeNode');
    m2.setAttribute('in', 'SourceGraphic');
    merge.appendChild(m1);
    merge.appendChild(m2);
    filter.appendChild(blur);
    filter.appendChild(merge);
    defs.appendChild(filter);
    svgEl.appendChild(defs);

    const g = document.createElementNS(svgNS, 'g');
    svgEl.appendChild(g);

    // ── Cluster halos + labels ───────────────────────────────────────────────
    clusters.forEach(({ category, cx, cy, haloR }) => {
      const color = getCategoryColor(category);

      const halo = document.createElementNS(svgNS, 'circle');
      halo.setAttribute('cx', String(cx));
      halo.setAttribute('cy', String(cy));
      halo.setAttribute('r', String(haloR));
      halo.setAttribute('fill', color);
      halo.setAttribute('opacity', '0.05');
      halo.setAttribute('pointer-events', 'none');
      g.appendChild(halo);

      const label = document.createElementNS(svgNS, 'text');
      label.setAttribute('x', String(cx));
      label.setAttribute('y', String(cy + haloR + 16));
      label.setAttribute('text-anchor', 'middle');
      label.setAttribute('font-size', '10.5');
      label.setAttribute('letter-spacing', '0.04em');
      label.setAttribute('font-family', 'ui-monospace, "SF Mono", Menlo, monospace');
      label.setAttribute('fill', color);
      label.setAttribute('opacity', '0.4');
      label.setAttribute('pointer-events', 'none');
      label.textContent = category;
      g.appendChild(label);
    });

    // ── Edges ────────────────────────────────────────────────────────────────
    links.forEach((link) => {
      const line = document.createElementNS(svgNS, 'line');
      line.setAttribute('x1', String(link.source.x));
      line.setAttribute('y1', String(link.source.y));
      line.setAttribute('x2', String(link.target.x));
      line.setAttribute('y2', String(link.target.y));
      line.setAttribute('stroke', '#ffffff');
      line.setAttribute('stroke-width', '1');
      line.setAttribute('opacity', String(0.06 + Math.min(link.weight - 2, 4) * 0.025));
      line.setAttribute('pointer-events', 'none');
      g.appendChild(line);
      link.el = line;
    });

    // ── Drag state ────────────────────────────────────────────────────────────
    let dragNode: GraphNode | null = null;
    let dragOffsetX = 0;
    let dragOffsetY = 0;
    let hasDragged = false;

    function updateEdgesForNode(node: GraphNode) {
      links.forEach((link) => {
        if (!link.el) return;
        if (link.source === node) {
          link.el.setAttribute('x1', String(node.x));
          link.el.setAttribute('y1', String(node.y));
        } else if (link.target === node) {
          link.el.setAttribute('x2', String(node.x));
          link.el.setAttribute('y2', String(node.y));
        }
      });
    }

    function onMouseMove(e: MouseEvent) {
      if (!dragNode) return;
      hasDragged = true;
      const rect = svgEl.getBoundingClientRect();
      dragNode.x = e.clientX - rect.left - dragOffsetX;
      dragNode.y = e.clientY - rect.top - dragOffsetY;
      if (dragNode.el) {
        dragNode.el.setAttribute('cx', String(dragNode.x));
        dragNode.el.setAttribute('cy', String(dragNode.y));
        dragNode.el.style.cursor = 'grabbing';
      }
      updateEdgesForNode(dragNode);
    }

    function onDragEnd() {
      if (dragNode?.el) dragNode.el.style.cursor = 'grab';
      dragNode = null;
    }

    svgEl.addEventListener('mousemove', onMouseMove);
    svgEl.addEventListener('mouseup', onDragEnd);
    svgEl.addEventListener('mouseleave', onDragEnd);

    // ── Age fade — compute date range once across all nodes ───────────────────
    const newestMs = Math.max(...nodes.map((n) => new Date(n.createdAt).getTime()));
    const oldestMs = Math.min(...nodes.map((n) => new Date(n.createdAt).getTime()));
    const rangeMs = newestMs - oldestMs;

    // ── Nodes ─────────────────────────────────────────────────────────────────
    nodes.forEach((node) => {
      const r = nodeRadius(node.relevanceScore);
      const baseColor = getCategoryColor(node.category);
      const fade = ageFade(node.createdAt, newestMs, rangeMs);
      const color = fadeColor(baseColor, fade);
      // Stroke also fades: newer nodes have a brighter white ring
      const strokeOpacity = 0.18 + (1 - fade) * 0.12;

      const circle = document.createElementNS(svgNS, 'circle');
      circle.setAttribute('cx', String(node.x));
      circle.setAttribute('cy', String(node.y));
      circle.setAttribute('r', String(r));
      circle.setAttribute('fill', color);
      circle.setAttribute('stroke', `rgba(255,255,255,${strokeOpacity.toFixed(2)})`);
      circle.setAttribute('stroke-width', '1.5');
      circle.style.cursor = 'grab';
      circle.style.transition = 'stroke 0.1s';

      circle.addEventListener('mouseenter', (e) => {
        if (dragNode) return;
        // Restore full vibrant colour on hover so faded old nodes still pop
        circle.setAttribute('fill', baseColor);
        circle.setAttribute('stroke', 'rgba(255,255,255,0.9)');
        circle.setAttribute('filter', 'url(#node-glow)');
        const rect = svgEl.getBoundingClientRect();
        setTooltip({
          node,
          x: (e as MouseEvent).clientX - rect.left,
          y: (e as MouseEvent).clientY - rect.top,
        });
      });

      circle.addEventListener('mouseleave', () => {
        if (dragNode === node) return;
        circle.setAttribute('fill', color); // restore faded colour
        circle.setAttribute('stroke', `rgba(255,255,255,${strokeOpacity.toFixed(2)})`);
        circle.removeAttribute('filter');
        setTooltip(null);
      });

      circle.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        hasDragged = false;
        const rect = svgEl.getBoundingClientRect();
        dragNode = node;
        dragOffsetX = (e as MouseEvent).clientX - rect.left - node.x;
        dragOffsetY = (e as MouseEvent).clientY - rect.top - node.y;
        circle.setAttribute('fill', baseColor);
        circle.setAttribute('stroke', 'rgba(255,255,255,0.9)');
        circle.setAttribute('filter', 'url(#node-glow)');
        setTooltip(null);
      });

      circle.addEventListener('click', (e) => {
        if (hasDragged) {
          e.stopPropagation();
          return;
        }
        navigate(`/insight/${node.id}`);
      });

      g.appendChild(circle);
      node.el = circle;
    });

    return () => {
      svgEl.removeEventListener('mousemove', onMouseMove);
      svgEl.removeEventListener('mouseup', onDragEnd);
      svgEl.removeEventListener('mouseleave', onDragEnd);
      while (svgEl.firstChild) svgEl.removeChild(svgEl.firstChild);
    };
  }, [allEntries, filteredCategory, navigate]);

  const categories = [...new Set(allEntries.map((e) => e.category))].sort();

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-screen">
      {/* Filter bar */}
      <div
        className="px-4 py-2.5 flex items-center gap-2 flex-wrap flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: '#0a0e14' }}
      >
        <span className="text-xs mr-1" style={{ color: '#3d4451' }}>Top {MAX_GRAPH_NODES}</span>
        <button
          onClick={() => setFilteredCategory(null)}
          className="text-xs px-3 py-1 rounded-full transition-all"
          style={
            filteredCategory === null
              ? { background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.7)', color: '#a5b4fc' }
              : { background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#4b5563' }
          }
        >
          All
        </button>
        {categories.map((cat) => {
          const color = getCategoryColor(cat);
          const active = filteredCategory === cat;
          return (
            <button
              key={cat}
              onClick={() => setFilteredCategory(active ? null : cat)}
              className="text-xs px-2.5 py-1 rounded-full transition-all flex items-center gap-1.5"
              style={
                active
                  ? { background: `${color}28`, border: `1px solid ${color}`, color }
                  : { background: `${color}12`, border: `1px solid ${color}38`, color: `${color}bb` }
              }
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: color,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              {cat}
            </button>
          );
        })}
      </div>

      {/* Canvas */}
      <div className="flex-1 relative overflow-hidden" style={{ background: '#0d1117' }}>
        {loading && (
          <div
            className="absolute inset-0 flex items-center justify-center text-sm"
            style={{ color: '#4b5563' }}
          >
            Loading graph...
          </div>
        )}
        <svg ref={svgRef} className="w-full h-full" />

        {/* Tooltip */}
        {tooltip && (() => {
          const color = getCategoryColor(tooltip.node.category);
          return (
            <div
              className="absolute pointer-events-none z-20 rounded-xl p-3 text-xs max-w-xs"
              style={{
                left: tooltip.x + 16,
                top: tooltip.y - 16,
                background: 'rgba(10,14,20,0.88)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: `1px solid ${color}44`,
                boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04), 0 0 20px ${color}18`,
              }}
            >
              <div
                className="font-semibold leading-snug mb-2"
                style={{ color: '#e2e8f0', fontSize: '12px' }}
              >
                {tooltip.node.title}
              </div>
              <div className="flex items-center gap-2">
                <span style={{ color: '#4b5563', fontSize: '11px' }}>{tooltip.node.category}</span>
                <span
                  className="font-semibold rounded-full px-1.5 py-0.5"
                  style={{
                    fontSize: '10px',
                    background: `${color}20`,
                    color,
                    border: `1px solid ${color}44`,
                  }}
                >
                  {tooltip.node.relevanceScore}/10
                </span>
              </div>
              <div className="mt-2" style={{ color: '#374151', fontSize: '11px' }}>
                Click to open →
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
